import { getLogger } from '@disy/lib/logging';

import type {
  MessagePayload,
  MessageType,
  SyncMessage,
  WindowsSyncEvent,
} from 'cadenza/multi-windows/synchronization/message-definitions';
import { getStore } from 'cadenza/store/cadenza-store';
import { getWindowsSyncChannelName, getWorkbookWindowType } from 'cadenza/workbook/workbook-core-selectors.state';
import type { WindowType } from 'cadenza/workbook/workbook.state';

const logger = getLogger('cadenza/multi-windows-sync');

const WINDOWS_SYNC_CHANNEL_REGISTRY: Record<string, WindowsSyncChannel> = {};

export function getWindowsSyncChannel(channelName: string): WindowsSyncChannel {
  if (!(channelName in WINDOWS_SYNC_CHANNEL_REGISTRY)) {
    const windowType = getWorkbookWindowType(getStore().getState());
    WINDOWS_SYNC_CHANNEL_REGISTRY[channelName] = new WindowsSyncChannel(channelName, windowType);
  }
  return WINDOWS_SYNC_CHANNEL_REGISTRY[channelName];
}

export function getCurrentWindowsSyncChannel() {
  const windowsSyncChannelName = getWindowsSyncChannelName(getStore().getState());
  return getWindowsSyncChannel(windowsSyncChannelName);
}

/**
 * Wrapper around an instance of BroadcastChannel.
 * - Provides convenience methods for sending messages and subscribing to specific types of messages.
 * - Adds typing for messages and their payloads
 * - Wraps and unwraps the messages' payloads.
 *
 * This class is used both on the primary window and on the secondary windows.
 */
class WindowsSyncChannel extends EventTarget {
  readonly #channel;
  readonly #windowType;

  constructor(channelName: string, windowType: WindowType) {
    super();
    this.#windowType = windowType;
    this.#channel = new BroadcastChannel(channelName);
    this.#setupChannelListener();
  }

  get name() {
    return this.#channel.name;
  }

  sendMessage<T extends MessageType>(messageType: T, payload: MessagePayload<T>) {
    logger.debug(`Sending message:`, { type: messageType, payload });
    const message: SyncMessage<T> = {
      type: messageType,
      recipientType: this.#otherWindowType,
      timestamp: Date.now(),
      payload,
    };
    this.#channel.postMessage(message);
  }

  subscribe<T extends MessageType>(messageType: T, listener: (payload: MessagePayload<T>) => void) {
    this.addEventListener(messageType, (event) => {
      const payload = (event as WindowsSyncEvent<T>).detail;
      listener(payload);
    });
  }

  #setupChannelListener() {
    this.#channel.addEventListener('message', (event: MessageEvent<SyncMessage>) => {
      const message = event.data;
      if (message.recipientType !== this.#windowType) {
        return;
      }

      logger.debug(`Received message:`, message);

      this.dispatchEvent(new CustomEvent(message.type as string, { detail: message.payload }));
    });
  }

  get #otherWindowType() {
    return this.#windowType === 'Primary' ? 'Secondary' : 'Primary';
  }
}

// Export class as type, class itself is not exported as we want to restrict creation
export type WindowsSyncChannelType = WindowsSyncChannel;
